import Core                      from "@/core/Core";
import Dictionary                from "@/core/types/Dictionary";
import VatRate                   from "@/core/types/VatRate";
import Partner                   from "@/domains/Partners/Partner";
import PaymentTermEvent          from "@/domains/PaymentTerms/PaymentTermEvent";
import PaymentPriorityType       from "@/domains/PaymentRequests/PaymentPriorityType";
import PaymentReasonBasis        from "@/domains/PaymentRequests/PaymentReasonBasis";
import StickerType               from "@/domains/Stickers/StickerType";
import StickerColor              from "@/domains/Stickers/StickerColor";
import DeliveryType              from "@/domains/DeliveryTypes/DeliveryType";
import ComplaintType             from "@/domains/ComplaintType/ComplaintType";
import EdiOperator               from "@/domains/EdiOperators/EdiOperator";
import CompanyTaxSystem          from "@/domains/CompanyTaxSystems/CompanyTaxSystem";
import Unit                      from "@/domains/Units/Unit";
import OrderCategory             from "@/domains/Orders/OrderCategory";
import PartnersService           from "@/domains/Partners/PartnersService";
import SupplyOffersService       from "@/domains/SupplyOffers/SupplyOffersService";
import OrdersService             from "@/domains/Orders/OrdersService";
import DeliveryTypesService      from "@/domains/DeliveryTypes/DeliveryTypesService";
import ComplaintTypesService     from "@/domains/ComplaintType/ComplaintTypesService";
import UnitsService              from "@/domains/Units/UnitsService";
import StickerTypesService       from "@/domains/Stickers/StickerTypesService";
import StickerColorsService      from "@/domains/Stickers/StickerColorsService";
import EdiOperatorsService       from "@/domains/EdiOperators/EdiOperatorsService";
import PaymentTermsService       from "@/domains/PaymentTerms/PaymentTermsService";
import PaymentRequestsService    from "@/domains/PaymentRequests/PaymentRequestsService";
import CompaniesService          from "@/domains/Companies/CompaniesService";
import CargoUnitsService          from "@/domains/CargoUnits/CargoUnitsService";
import SWPortalTasksService      from "@/domains/SWPortalTask/SWPortalTasksService";
import PriorityOption            from "@/domains/SWPortalTask/PriorityOption";
import PartnerCategoriesService  from "@/domains/PartnerCategories/PartnerCategoriesService";
import PartnerCategory           from "@/domains/PartnerCategories/PartnerCategory";
import SupplyOfferStatus         from "@/domains/SupplyOffers/SupplyOfferStatus";
import ProductActualStatus       from "@/domains/Products/ProductActualStatus";
import ProductsService           from "@/domains/Products/ProductsService";
import PalletType                from "@/domains/CargoUnits/PalletType";
import ProductCategoriesService  from "@/domains/ProductCategories/ProductCategoriesService";
import VehiclesService           from "@/domains/Vehicles/VehiclesService";
import VehicleOwnershipType      from "@/domains/Vehicles/VehicleOwnershipType";

/**
 * Справочники приложения
 * Загружаются один раз при инициализации и доступны глобально
 */
class Dictionaries {
  private static isInitialized = false;

  // ============================================================
  // Прокси на кеши доменных сервисов.
  // Временный совместимостный слой: данные живут в сервисах, здесь — только
  // чтение, чтобы существующие потребители Core.dictionaries.X не переписывать.
  // Удаляются по мере перевода потребителей на сервисы напрямую.
  // ============================================================
  static get companyTaxSystems()                   { return CompaniesService.companyTaxSystems; }
  static get complaintTypes()                      { return ComplaintTypesService.complaintTypes; }
  static get deliveryTypes()                       { return DeliveryTypesService.deliveryTypes; }
  static get ediOperators()                        { return EdiOperatorsService.ediOperators; }
  static get orderCategories()                     { return OrdersService.orderCategories; }
  static get palletTypesList()                     { return CargoUnitsService.palletTypesList; }
  static get partnerCategories()                   { return PartnerCategoriesService.partnerCategories; }
  static get paymentPriorityTypes()                { return PaymentRequestsService.paymentPriorityTypes; }
  static get paymentReasonBases()                  { return PaymentRequestsService.paymentReasonBases; }
  static get paymentTermEvents()                   { return PaymentTermsService.paymentTermEvents; }
  static get priorityOptions()                     { return SWPortalTasksService.priorityOptions; }
  static get productActualStatusList()             { return ProductsService.productActualStatusList; }
  static get stickerColors()                       { return StickerColorsService.stickerColors; }
  static get stickerTypes()                        { return StickerTypesService.stickerTypes; }
  static get supplyOfferStatuses()                 { return SupplyOffersService.statuses; }
  static get supplyOfferStatusesAvailability()     { return SupplyOffersService.statusesAvailability; }
  static get units()                               { return UnitsService.units; }

  // static currenciesList: any[] = [];
  static tenderRefusalReasons: Dictionary<any>;
  /** Ставки НДС. VatRate — расчётный Numeric-тип, он же элемент справочника:
   *  id даёт сам процент, toString — «20%» для селектов. */
  static vatRates: Dictionary<number | string, VatRate>;
  static vatInclusionOptions = Dictionary.fromArray([
    { id: 'yes', value: true, name: 'включительно', toString() { return this.name } },
    { id: 'no', value: false, name: 'сверху', toString() { return this.name } },
  ]);

  static sibWellPartner: Partner;

  // todo появилось подозрение, что эти словари смотрятся уместнее в своём домене TendersPageFilter
  // todo если продолжать всё складывать в одном файле, то он разрастётся и навигация затруднится
  // ================= TENDER =================
  // static tenderHasBids: Dictionary<any> = Dictionary.fromArray([
  //     { id: 1, name: 'Все', value: null },
  //     { id: 2, name: 'Есть ставки', value: true },
  //     { id: 3, name: 'Нет ставок', value: false }
  // ]);

  static tenderIsPreorderNeeded: Dictionary<any> = Dictionary.fromArray([
    { id: 1, name: 'По предзаказу', value: true },
    { id: 2, name: 'Обычные', value: false },
    { id: 3, name: 'Все', value: null }
  ]);
  // Привилегированный пользователь может менять фильтр по покупателю
  // Каждый раз делать запрос на два айдишника избыточно ввиду плачевного состояния интернета.
  // Гораздо проще иметь двух покупателей в виде { id, name },
  // потому что для фильтра больше данных не нужно
  static readonly tenderBuyers: Dictionary<any> = Dictionary.fromArray([
    {id: 67, name: 'Сибирское здоровье'},
    {id: 559, name: 'Технологии красоты, ООО'}
  ]);


  // ================= ORDERS =================

  static orderIsOfferBased: Dictionary<any> = Dictionary.fromArray([
    { id: 1, name: 'Прямые', value: false },
    { id: 2, name: 'На основе предложения', value: true },
    { id: 3, name: 'Все', value: null }
  ]);

  static orderIsSamples: Dictionary<any> = Dictionary.fromArray([
    { id: 1, name: 'Образцы', value: true },
    { id: 2, name: 'Обычные', value: false },
    { id: 3, name: 'Все', value: null }
  ]);

  static orderIsForTestsOnly: Dictionary<any> = Dictionary.fromArray([
    { id: 1, name: 'Идут под план', value: false },
    { id: 2, name: 'Не идут под план', value: true },
    { id: 3, name: 'Все', value: null }
  ]);

  static orderDeliveryStatus: Dictionary<any> = Dictionary.fromArray([
    { id: 'all', name: 'Все' },
    { id: 'in-work', name: 'В работе' },
    { id: 'delivery', name: 'Отгруженные' },
    { id: 'received', name: 'Полученные' }
  ]);

  static orderPaymentStatus: Dictionary<any> = Dictionary.fromArray([
    { id: 'all', name: 'Все' },
    { id: 'no-request', name: 'Без заявок' },
    { id: 'not-paid', name: 'Неоплаченные' },
    { id: 'paid', name: 'Оплаченные' }
  ]);

  /**
   * Инициализация справочников
   */
  static async init() {
    if (this.isInitialized) {
      Core.error('Dictionaries уже инициализирован');
      return this;
    }

    try {
      // инициализируем только если пользователь залогинен, поскольку нам нужно знать для какой страны получать данные
      Core.eventBus.AclLogin.addEventListener(() => this.getCombinedFrontendData());
      Core.eventBus.AclSessionRestored.addEventListener(() => this.getCombinedFrontendData());
      // и очищаем кеш если пользователь выходит, он может зайти под другим партнером
      Core.eventBus.AclSessionExpired.addEventListener(() => this.clear());
      Core.eventBus.AclLogout.addEventListener(() => this.clear());

      this.isInitialized = true;
      //Core.debug('📚 Dictionaries инициализирован');
    } catch (error) {
      Core.error('Ошибка инициализации Dictionaries:', error);
    }

    await this.getCombinedFrontendData();

  }


  private static async getCombinedFrontendData(): Promise<void> {
    if (Core.acl.currentUser === null) return;

    /** Update от 12.12.2025; при логине под Наманганом считаем сам Наманган приоритетным покупателем  */
    if (Core.currentPartner?.isBuyer) {
      this.sibWellPartner = await PartnersService.get(Core.currentPartner.id) as Partner;
    } else {
      /** А для поставщиков пусть подкидывается СибВалео  **/
      this.sibWellPartner = await PartnersService.get(Core.config.sibWellPartnerId) as Partner;
    }

    const { data } = await Core.legacyRpc('utilities.getCombinedFrontendData', {
      localCurrencyCode: Core.currentPartner!.country.localCurrency.code || Core.config.localCurrency.code,
      countryCode: Core.currentPartner!.country.code || Core.config.localCountry.code,
    });

    CargoUnitsService.palletTypesList.reset(Core.utils.hydrateArray(data.palletTypesList, PalletType.fromServer));
    CompaniesService.companyTaxSystems.reset(Core.utils.hydrateArray(data.companyTaxSystemsList, CompanyTaxSystem.fromServer));
    ComplaintTypesService.complaintTypes.reset(Core.utils.hydrateArray(data.complaintTypesList, ComplaintType.fromServer));
    DeliveryTypesService.deliveryTypes.reset(Core.utils.hydrateArray(data.deliveryTypes, DeliveryType.fromServer));
    EdiOperatorsService.ediOperators.reset(Core.utils.hydrateArray(data.ediOperatorsList, EdiOperator.fromServer));
    OrdersService.orderCategories.reset(Core.utils.hydrateArray(data.orderCategories, OrderCategory.fromServer));
    PartnerCategoriesService.partnerCategories.reset(Core.utils.hydrateArray(data.partnerCategories, PartnerCategory.fromServer));
    PaymentRequestsService.paymentPriorityTypes.reset(Core.utils.hydrateArray(data.paymentRequestPriorityTypes, PaymentPriorityType.fromServer));
    PaymentRequestsService.paymentReasonBases.reset(Core.utils.hydrateArray(data.paymentReasonBases, PaymentReasonBasis.fromServer));
    PaymentTermsService.paymentTermEvents.reset(Core.utils.hydrateArray(data.paymentTermEvents, PaymentTermEvent.fromServer));
    StickerColorsService.stickerColors.reset(Core.utils.hydrateArray(data.stickerColorsList, StickerColor.fromServer));
    StickerTypesService.stickerTypes.reset(Core.utils.hydrateArray(data.stickerTypesList, StickerType.fromServer));
    SupplyOffersService.statuses.reset(Core.utils.hydrateArray(data.supplyOfferStatuses, SupplyOfferStatus.fromServer));
    SupplyOffersService.statusesAvailability.reset(Core.utils.hydrateArray(data.supplyOfferStatusesAvailability, SupplyOfferStatus.fromServer));
    SWPortalTasksService.priorityOptions.reset(Core.utils.hydrateArray(data.priorityOptions, PriorityOption.fromServer));
    UnitsService.units.reset(Core.utils.hydrateArray(data.units, Unit.fromServer));
    ProductsService.productActualStatusList.reset(Core.utils.hydrateArray(data.productActualStatusList, ProductActualStatus.fromServer));
    VehiclesService.vehicleOwnershipTypes.reset(Core.utils.hydrateArray(data.vehicleOwnershipTypesList, VehicleOwnershipType.fromServer));

    this.tenderRefusalReasons = Dictionary.fromArray(data.tenderRefusalReasonsList);
    this.vatRates =  Dictionary.fromArray(Core.utils.hydrateArray(data.vatRates, VatRate.fromServer));

    Core.eventBus.DictionariesDownloaded.emit();
  }

  /**
   * Очистка всех справочников
   */
  private static clear(): void {

    EdiOperatorsService.ediOperators.clear();
    CargoUnitsService.palletTypesList.clear();
    CompaniesService.companyTaxSystems.clear();
    ComplaintTypesService.complaintTypes.clear();
    DeliveryTypesService.deliveryTypes.clear();
    PaymentTermsService.paymentTermEvents.clear();
    PartnerCategoriesService.partnerCategories.clear();
    PaymentRequestsService.paymentPriorityTypes.clear();
    OrdersService.orderCategories.clear();
    StickerTypesService.stickerTypes.clear();
    StickerColorsService.stickerColors.clear();
    SWPortalTasksService.priorityOptions.clear();
    PaymentRequestsService.paymentReasonBases.clear();
    UnitsService.units.clear();
    SupplyOffersService.statusesAvailability.clear();
    SupplyOffersService.statuses.clear();
    ProductsService.productActualStatusList.clear();
    VehiclesService.vehicleOwnershipTypes.clear();
    ProductCategoriesService.resetProductCategoriesMap();

    this.tenderRefusalReasons.clear();
    this.vatRates.clear();

    this.sibWellPartner = null;

    // Core.debug('📚 Dictionaries удалены');
  }
}

export default Dictionaries;